import apis from './config';

const imageApi = apis.image;

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
// Read max file size from environment variable (in MB), default to 10MB
const MAX_FILE_SIZE = (parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 10) * 1024 * 1024;

export class ImageValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ImageValidationError';
    }
}

const validateImage = (file) => {
    if (!file) {
        throw new ImageValidationError('No file provided');
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        throw new ImageValidationError(`Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.join(', ')}`);
    }

    if (file.size > MAX_FILE_SIZE) {
        throw new ImageValidationError(`File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
    }
};

export const uploadImage = async (file) => {
    try {
        validateImage(file);

        const { options } = file;

        const formData = new FormData();
        formData.append('image', file);

        // Add optional metadata
        if (options.alt) formData.append('alt', options.alt);
        if (options.filename) formData.append('filename', options.filename);

        const response = await imageApi.post('/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            retag: true,
            validateCsrf: true,
            onUploadProgress: (progressEvent) => {
                if (file.onProgress) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    file.onProgress(percentCompleted);
                }
            },
        });

        return {
            id: response.data.id,
            url: response.data.url,
            filename: response.data.filename,
            alt: response.data.alt,
            format: response.data.format,
        };
    } catch (error) {
        if (error instanceof ImageValidationError) {
            throw error;
        }
        console.error('Error uploading image:', error);
        throw new Error('Failed to upload image');
    }
};

export const getImageById = async (id) => {
    try {
        const response = await imageApi.get(`/media/images/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching image:', error);
        throw error;
    }
};

export const deleteImage = async (id) => {
    try {
        await imageApi.delete(`/media/images/${id}`, {
            retag: true,
            validateCsrf: true,
        });
    } catch (error) {
        console.error('Error deleting image:', error);
        throw error;
    }
};
